/**
 * Backend error → human-readable message helpers.
 *
 * Why this file exists
 * --------------------
 * Django / DRF can return errors in a LOT of different shapes:
 *
 *   1. `{ "detail": "Authentication credentials were not provided." }`
 *   2. `{ "non_field_errors": ["Invalid credentials."] }`
 *   3. `{ "email": ["This field is required."],
 *         "password": ["Too short."] }`
 *   4. `{ "errors": [{ "code": "x", "detail": "y" }, ...] }`           (DRF 3.14+)
 *   5. A raw HTML 500 page (when DEBUG=False the dev server still emits one).
 *   6. A network error (no `response` at all — server down / CORS / timeout).
 *   7. A validation error wrapped in nested objects:
 *         `{ "user": { "email": ["..."] } }`
 *
 * The whole frontend deserves to SEE every one of these — the user's brief is
 * "any and all errors that are there in the backend should be displayed in
 * the frontend as well, so that we can look into it."
 *
 * So this module gives us:
 *
 *   * `formatApiError(error)`   -> single string ready for a toast / banner.
 *   * `extractFieldErrors(err)` -> { fieldName: "msg" } for inline form hints.
 *   * `errorToDetails(err)`     -> rich object with status, method, url, body
 *                                  and a `lines` array — used by <ErrorBox>
 *                                  to render structured errors as a list.
 */

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walk an arbitrary DRF error payload and yield "key: message" strings.
 *
 * - strings / numbers / booleans   -> "<value>"
 * - arrays                          -> recurse on each element
 * - plain objects                   -> recurse with "<parent>.<key>" prefix
 *
 * The `prefix` argument keeps track of the dotted path for nested fields.
 */
function* walkPayload(node, prefix = '') {
  if (node === null || node === undefined) return;

  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    yield prefix ? `${prefix}: ${node}` : String(node);
    return;
  }

  if (Array.isArray(node)) {
    for (const item of node) yield* walkPayload(item, prefix);
    return;
  }

  if (typeof node === 'object') {
    // DRF "errors" array of { code, detail, attr } objects
    if (Array.isArray(node.errors)) {
      for (const e of node.errors) {
        const attr = e.attr || e.field || '';
        const msg  = e.detail || e.message || JSON.stringify(e);
        yield attr ? `${attr}: ${msg}` : String(msg);
      }
      return;
    }

    for (const [key, value] of Object.entries(node)) {
      const nextPrefix = prefix ? `${prefix}.${key}` : key;
      // `detail`, `message` and `non_field_errors` read better without a prefix.
      const flatten = key === 'detail' || key === 'message' || key === 'non_field_errors';
      yield* walkPayload(value, flatten ? prefix : nextPrefix);
    }
  }
}

/** Strip HTML tags from an HTML 500 response so we can show plain text. */
function htmlToText(html) {
  if (typeof html !== 'string') return String(html);
  // Pull out the <title> first — Django's debug page has a usable title.
  const titleMatch = html.match(/<title>([^<]+)<\/title>/i);
  const title = titleMatch ? titleMatch[1].trim() : '';
  const text  = html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // Title is usually the most useful single line.
  return title || text.slice(0, 500);
}

function loginSecurityPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (data.code !== 'login_security') return null;
  return data;
}

function formatMinutesLabel(n) {
  const minutes = Number(n);
  if (!Number.isFinite(minutes) || minutes < 1) return '';
  const rounded = Math.max(1, Math.ceil(minutes));
  return `${rounded} ${rounded === 1 ? 'minute' : 'minutes'}`;
}

/**
 * Structured login-security lock (account / IP / device).
 * Returns a title + reason lines for ErrorBox, or null.
 */
export function loginSecurityDetails(data) {
  const payload = loginSecurityPayload(data);
  if (!payload) return null;
  const title = payload.title || 'Login blocked';
  const lines = [];
  if (payload.lock_reason) lines.push(`Reason: ${payload.lock_reason}`);
  if (payload.layer_label) lines.push(`Security layer: ${payload.layer_label}`);
  const wait = formatMinutesLabel(payload.retry_after_minutes);
  if (wait) {
    lines.push(`Try again in ${wait}.`);
  } else if (payload.detail && payload.detail !== payload.lock_reason) {
    lines.push(String(payload.detail));
  }
  if (lines.length === 0 && payload.detail) lines.push(String(payload.detail));
  return { title, lines, hideTrail: true };
}

function formatLoginSecurityMessage(data) {
  const details = loginSecurityDetails(data);
  if (!details) return null;
  const body = details.lines.length ? details.lines.join(' ') : '';
  return body ? `${details.title}. ${body}` : details.title;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Turn any error (axios / generic Error / string / null) into a single
 * human-readable string suitable for a toast.
 *
 * Always returns a non-empty string — even if the error is `null`, you'll
 * get "Unknown error".  That way callers don't need null-checks.
 */
export function formatApiError(error) {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;

  const response = error.response;

  // --- network / no response ------------------------------------------------
  if (!response) {
    if (error.code === 'ECONNABORTED') {
      return `Request timed out${error.config?.url ? ` (${error.config.url})` : ''}`;
    }
    if (error.message === 'Network Error') {
      return 'Network error — could not reach the server. Check your connection.';
    }
    return error.message || String(error);
  }

  const security = formatLoginSecurityMessage(response.data);
  if (security) return security;

  const { status, data } = response;
  const lines = [];

  if (typeof data === 'string') {
    // HTML 500 page or plain-text response.
    const stripped = data.includes('<') ? htmlToText(data) : data;
    if (stripped) lines.push(stripped);
  } else if (data && typeof data === 'object') {
    for (const line of walkPayload(data)) lines.push(line);
  }

  if (lines.length === 0) {
    // Fall back to status text if nothing else was usable.
    lines.push(response.statusText || `HTTP ${status}`);
  }

  return `[${status}] ${lines.join(' | ')}`;
}

/**
 * Extract per-field error strings from a DRF validation response.
 * Useful for `<ResourceForm>` to highlight individual inputs.
 *
 * Returns a plain object `{ fieldName: "first error message" }`.
 * Non-validation errors (network, 500, etc.) yield an empty object.
 */
export function extractFieldErrors(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};

  const out = {};
  for (const [field, value] of Object.entries(data)) {
    if (field === 'detail' || field === 'non_field_errors') continue;
    if (field === 'code' || field === 'layer' || field === 'layer_label'
        || field === 'title' || field === 'lock_reason'
        || field === 'retry_after_minutes') continue;
    if (Array.isArray(value)) {
      out[field] = String(value[0] ?? '');
    } else if (typeof value === 'string') {
      out[field] = value;
    } else if (value && typeof value === 'object') {
      // Nested object — flatten one level so the user sees something useful.
      const inner = Object.values(value)[0];
      out[field] = Array.isArray(inner) ? String(inner[0] ?? '') : String(inner ?? '');
    }
  }
  return out;
}

/**
 * Rich detail object used by <ErrorBox /> to render structured errors.
 * Always returns the same shape so render code is trivial.
 */
export function errorToDetails(error) {
  if (!error) {
    return { title: 'Unknown error', status: null, method: '', url: '', lines: [] };
  }
  if (typeof error === 'string') {
    return { title: error, status: null, method: '', url: '', lines: [] };
  }

  const response = error.response;
  const cfg      = error.config || {};
  const method   = (cfg.method || '').toUpperCase();
  const url      = cfg.url || '';

  if (!response) {
    return {
      title:  error.message || 'Network error',
      status: null,
      method,
      url,
      lines:  [],
    };
  }

  const security = loginSecurityDetails(response.data);
  if (security) {
    return {
      title: security.title,
      status: response.status,
      method,
      url,
      lines: security.lines,
      hideTrail: true,
    };
  }

  const { status, data } = response;
  const lines = [];

  if (typeof data === 'string') {
    const stripped = data.includes('<') ? htmlToText(data) : data;
    if (stripped) lines.push(stripped);
  } else if (data && typeof data === 'object') {
    for (const line of walkPayload(data)) lines.push(line);
  }

  return {
    title:  response.statusText || `HTTP ${status}`,
    status,
    method,
    url,
    lines,
    hideTrail: false,
  };
}
